import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MatchingService, type MatchResponse, type GuideDetails } from '../../services/matchingService';
import { getSupabaseClient } from '../../lib/supabase';
import { Footer } from '@ui/components/Footer';
import { SeekerHeader } from '@ui/components/SeekerHeader';
import './FindingMatch.css';

type FindingMatchProps = {
  onComplete?: (matchResult: {
    found: boolean;
    guide?: GuideDetails;
    guides?: GuideDetails[];
    matchScore?: number;
    matchScores?: Record<string, number>;
    seekerVisibleMessages?: string[];
    seekerVisibleMessagesByGuide?: Record<string, string[]>;
  }) => void;
};

// How many guides to show on the match result page.
// Matches the Figma / phase-1 design: exactly two potential matches.
const MAX_GUIDES_TO_SHOW = 2;

export function FindingMatch({ onComplete }: FindingMatchProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSearching, setIsSearching] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const supabase = getSupabaseClient();

  const totalSteps = 5;

  // Perform the actual matching
  const resolveSeekerRecordId = async (): Promise<string | null> => {
    try {
      const {
        data: { session }
      } = await supabase.auth.getSession();
      const authUserId = session?.user?.id;

      if (authUserId) {
        const { data: seekerRow, error: seekerError } = await supabase
          .from('seekers')
          .select('seeker_id')
          .eq('user_id', authUserId)
          .maybeSingle();

        if (!seekerError && seekerRow?.seeker_id) {
          return seekerRow.seeker_id;
        }

        if (seekerError) {
          console.warn('Unable to resolve seeker_id from seekers table; falling back to session storage.', seekerError);
        }
      }
    } catch (authError) {
      console.warn('Unable to determine authenticated seeker.', authError);
    }

    return MatchingService.getSeekerIdFromSession();
  };

  const performMatching = async () => {
    try {
      const seekerId = await resolveSeekerRecordId();
      if (!seekerId) {
        throw new Error('No seeker session found. Please complete onboarding first.');
      }

      // Find matches — request top N guides for multi-guide display
      const matchResponse: MatchResponse = await MatchingService.findMatches(seekerId, MAX_GUIDES_TO_SHOW);

      if (matchResponse.matches && matchResponse.matches.length > 0) {
        // Build the multi-guide shape expected by GuideMatch / MatchResultPage
        const guidesArr: GuideDetails[] = [];
        const matchScores: Record<string, number> = {};
        const seekerVisibleMessagesByGuide: Record<string, string[]> = {};

        for (const m of matchResponse.matches) {
          const guideData = (m as any).guide_details;
          const guideDetails: GuideDetails = guideData
            ? {
                guide_id: guideData.guide_id,
                name: guideData.name,
                location: guideData.location,
                bio: guideData.bio,
                experience_types: guideData.experience_types,
                support_types: guideData.support_types,
                focus_areas: guideData.focus_areas,
                special_groups: guideData.special_groups,
                religious_beliefs: guideData.religious_beliefs,
              }
            : {
                guide_id: m.guide_id,
                name: 'Your Matched Guide',
                location: 'Available in your area',
                bio: 'A guide who matches your preferences and needs.',
                experience_types: ['1:1 journeys', 'Integration support'],
                support_types: ['Macrodose support'],
                focus_areas: ['Anxiety', 'Depression', 'Personal Growth'],
                special_groups: [],
                religious_beliefs: [],
              };

          guidesArr.push(guideDetails);
          matchScores[guideDetails.guide_id] = m.total_score;
          seekerVisibleMessagesByGuide[guideDetails.guide_id] = (m as any).seeker_visible_messages || [];
        }

        const bestMatch = matchResponse.matches[0];
        const bestGuide = guidesArr[0];

        const result = {
          found: true,
          // New multi-guide shape
          guides: guidesArr,
          matchScores,
          seekerVisibleMessagesByGuide,
          // Backwards-compat singular fields — consumers reading the old shape still work
          guide: bestGuide,
          matchScore: bestMatch.total_score,
          reasoning: bestMatch.reasoning || [],
          seekerVisibleMessages: (bestMatch as any).seeker_visible_messages || [],
        };

        console.log('FindingMatch returning multi-guide result:', result);

        // Store in localStorage for persistence
        localStorage.setItem('matching_result', JSON.stringify(result));

        return result;
      } else {
        return {
          found: false,
        };
      }
    } catch (err) {
      console.error('Matching error:', err);
      setError(err instanceof Error ? err.message : 'Failed to find matches');
      return {
        found: false
      };
    }
  };

  // Progress through steps
  useEffect(() => {
    if (!isSearching) return;

    const stepInterval = setInterval(() => {
      setCurrentStep(prev => {
        if (prev >= totalSteps - 1) {
          clearInterval(stepInterval);

          // After final step, perform the actual matching
          setTimeout(async () => {
            const matchResult = await performMatching();
            setIsSearching(false);

            if (onComplete) {
              onComplete(matchResult);
            } else {
              // Default behavior - navigate based on result
              // Use replace: true to prevent user from going back to the finding match screen
              if (matchResult.found && 'guide' in matchResult && 'matchScore' in matchResult) {
                navigate('/onboarding/match', {
                  replace: true,
                  state: {
                    guide: matchResult.guide,
                    matchScore: matchResult.matchScore
                  }
                });
              } else {
                navigate('/onboarding/no-match', { replace: true });
              }
            }
          }, 1000);
          return prev;
        }
        return prev + 1;
      });
    }, 2000); // 2 seconds per step

    return () => clearInterval(stepInterval);
  }, [navigate, onComplete, isSearching]);

  return (
    <div className="flex min-h-screen w-full flex-col">
      {/* Mobile header */}
      <header className="flex lg:hidden h-16 shrink-0 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
        <div className="w-10" aria-hidden />
        <div className="flex flex-1 justify-center">
          <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
        </div>
        <div className="w-10" aria-hidden />
      </header>

      {/* Desktop header */}
      <div className="hidden lg:block">
        <SeekerHeader />
      </div>

      {/* Main content */}
      <div className="finding-match-viewport flex-1">
        {/* Floating avatar circles - outside container so they fill the viewport */}
        <div className="floating-avatars">
          <div className="floating-avatar floating-avatar-1">
            <img src="/1.webp" alt="" className="floating-avatar-img" />
          </div>
          <div className="floating-avatar floating-avatar-2">
            <img src="/2.webp" alt="" className="floating-avatar-img" />
          </div>
          <div className="floating-avatar floating-avatar-3">
            <img src="/3.webp" alt="" className="floating-avatar-img" />
          </div>
          <div className="floating-avatar floating-avatar-4">
            <img src="/4.webp" alt="" className="floating-avatar-img" />
          </div>
          <div className="floating-avatar floating-avatar-5">
            <img src="/5.webp" alt="" className="floating-avatar-img" />
          </div>
          <div className="floating-avatar floating-avatar-6">
            <img src="/6.webp" alt="" className="floating-avatar-img" />
          </div>
          <div className="floating-avatar floating-avatar-7">
            <img src="/7.webp" alt="" className="floating-avatar-img" />
          </div>
          <div className="floating-avatar floating-avatar-8">
            <img src="/8.webp" alt="" className="floating-avatar-img" />
          </div>
        </div>

        <div className="finding-match-container">
          {/* Main title */}
          <h1 className="finding-match-title">
            Finding aligned guides
          </h1>

          {/* Subtext */}
          <p className="mt-4 text-base text-[#FFF6D2]/80 text-center">
            You'll be able to explore available listings next
          </p>

          {/* Progress bar */}
          <div className="finding-match-progress-container">
            <div
              className="finding-match-progress-bar"
              style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
            />
          </div>

          {/* Error message if any */}
          {error && (
            <p className="finding-match-error">
              {error}
            </p>
          )}
        </div>
      </div>

      {/* Site-wide footer - only visible on large screens */}
      <div className="hidden lg:block">
        <Footer />
      </div>
    </div>
  );
}
